import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import resumeData from '../data/resumeData.json';

const initialFormState = {
  name: '',
  email: '',
  message: ''
};

const Footer = ({ data = resumeData.footer }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState(null);

  const resolvedApiBaseUrl = useMemo(() => process.env.REACT_APP_API_BASE_URL || 'https://api.dlyn.me', []);

  const openModal = () => setIsModalOpen(true);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData(initialFormState);
    setErrors({});
    setNotification(null);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.name.trim()) {
      nextErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = 'Please enter a valid email address';
    }

    if (!formData.message.trim()) {
      nextErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      nextErrors.message = 'Message must be at least 10 characters long';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${resolvedApiBaseUrl}${data.contactApi.endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const payload = await response.json();

      if (response.ok && payload.status === data.contactApi.successStatus) {
        setNotification({
          type: 'success',
          message: "Thank you! Your message has been sent successfully. I'll get back to you soon!"
        });

        setFormData(initialFormState);

        setTimeout(() => {
          handleCloseModal();
          setNotification(null);
        }, data.closeAfterSuccessMs);
      } else {
        setNotification({
          type: 'error',
          message: payload.error || 'Failed to send message. Please try again later.'
        });
      }
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!notification) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setNotification(null);
    }, 5000);

    return () => clearTimeout(timer);
  }, [notification]);

  const customStyles = {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    content: {
      position: 'relative',
      top: 'auto',
      left: 'auto',
      right: 'auto',
      bottom: 'auto',
      border: 'none',
      background: 'transparent',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
      borderRadius: '10px',
      outline: 'none',
      padding: '0',
      maxWidth: '500px',
      width: '90%',
      maxHeight: '90vh'
    }
  };

  const contactButtonStyle = {
    background: 'linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)',
    color: '#fff',
    border: 'none',
    padding: '14px 30px',
    borderRadius: '999px',
    fontSize: '1.05rem',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 12px 26px rgba(13, 110, 253, 0.28)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
  };

  const modalCardStyle = {
    background: '#ffffff',
    borderRadius: '20px',
    padding: '34px 30px',
    position: 'relative',
    fontFamily: 'Roboto, sans-serif',
    border: '1px solid #e9edf3',
    boxShadow: '0 24px 70px rgba(15, 23, 42, 0.28)'
  };

  const closeButtonStyle = {
    position: 'absolute',
    top: '12px',
    right: '12px',
    background: '#f3f6fb',
    border: '1px solid #e6ebf3',
    borderRadius: '999px',
    width: '34px',
    height: '34px',
    cursor: 'pointer',
    color: '#4e5969',
    fontSize: '1rem'
  };

  const notificationStyle = {
    marginBottom: '18px',
    borderRadius: '10px',
    color: '#fff',
    padding: '11px 13px',
    fontSize: '0.95rem',
    fontWeight: 500
  };

  const formStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const labelStyle = {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#334155',
    marginTop: '10px'
  };

  const inputStyle = {
    border: '1px solid #d8e0eb',
    borderRadius: '10px',
    height: '44px',
    padding: '0 13px',
    fontSize: '0.95rem',
    outline: 'none',
    color: '#1e293b',
    backgroundColor: '#fbfdff'
  };

  const textareaStyle = {
    border: '1px solid #d8e0eb',
    borderRadius: '10px',
    minHeight: '130px',
    padding: '11px 13px',
    fontSize: '0.95rem',
    outline: 'none',
    resize: 'vertical',
    color: '#1e293b',
    backgroundColor: '#fbfdff'
  };

  const submitButtonStyle = {
    marginTop: '16px',
    border: 'none',
    borderRadius: '10px',
    height: '46px',
    fontWeight: 600,
    fontSize: '0.98rem',
    color: '#fff',
    background: 'linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)',
    cursor: 'pointer'
  };

  return (
    <section className="o-section t-section o-section--footer">
      <div className="o-section__header-bg t-section__header"></div>
      <div className="o-section__content-bg t-section__content"></div>

      <div className="o-container">
        <div className="o-section__container">
          <header className="o-section__header t-section__header">
            <div className="o-content">
              <h2 className="o-section__heading">{data.heading}</h2>
              <div className="o-content__body o-section__description">{data.description}</div>
            </div>
          </header>

          <div className="o-section__content t-section__content">
            <div className="c-footer__contact" style={{ textAlign: 'center', marginBottom: '30px' }}>
              <button
                onClick={openModal}
                style={contactButtonStyle}
                aria-label="Open contact form"
              >
                <i className="fas fa-envelope" style={{ marginRight: '10px', color: '#fff' }}></i>
                {data.ctaLabel}
              </button>
            </div>

            <hr className="c-footer__contact-divider" />

            <Modal
              isOpen={isModalOpen}
              onRequestClose={handleCloseModal}
              style={customStyles}
              contentLabel="Contact Form Modal"
              closeTimeoutMS={300}
            >
              <div style={modalCardStyle}>
                <button
                  onClick={handleCloseModal}
                  style={closeButtonStyle}
                  aria-label="Close contact form"
                >
                  <i className="fas fa-times"></i>
                </button>

                {notification ? (
                  <div
                    style={{
                      ...notificationStyle,
                      background: notification.type === 'success' ? '#2d9c5a' : '#c0392b'
                    }}
                  >
                    {notification.message}
                  </div>
                ) : null}

                <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                  <h2 style={{ margin: '0 0 8px 0', color: '#0f172a', fontSize: '1.8rem', fontWeight: 500 }}>
                    <i className="fas fa-envelope" style={{ color: '#007bff', marginRight: '15px' }}></i>
                    {data.modalTitle}
                  </h2>
                  <p style={{ color: '#64748b', fontSize: '0.98rem', lineHeight: 1.5 }}>{data.modalDescription}</p>
                </div>

                <form onSubmit={handleSubmit} style={formStyle}>
                  <label htmlFor="name" style={labelStyle}>Name</label>
                  <input id="name" name="name" value={formData.name} onChange={handleInputChange} disabled={isSubmitting} style={inputStyle} />
                  {errors.name ? <small style={{ color: '#dc3545', fontSize: '0.82rem' }}>{errors.name}</small> : null}

                  <label htmlFor="email" style={labelStyle}>Email</label>
                  <input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} disabled={isSubmitting} style={inputStyle} />
                  {errors.email ? <small style={{ color: '#dc3545', fontSize: '0.82rem' }}>{errors.email}</small> : null}

                  <label htmlFor="message" style={labelStyle}>Message</label>
                  <textarea id="message" name="message" rows="6" value={formData.message} onChange={handleInputChange} disabled={isSubmitting} style={textareaStyle}></textarea>
                  {errors.message ? <small style={{ color: '#dc3545', fontSize: '0.82rem' }}>{errors.message}</small> : null}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      ...submitButtonStyle,
                      opacity: isSubmitting ? 0.75 : 1,
                      boxShadow: isSubmitting ? 'none' : '0 12px 24px rgba(13, 110, 253, 0.28)'
                    }}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </Modal>

            <div className="o-content">
              <div className="c-footer__bottom">
                <div className="c-footer__brand">
                  <div className="c-brand">
                    <div className="o-content__body">
                      <h1 className="c-brand__title t-title">
                        <span className="c-brand__sizer c-brand__sizer--sm">
                          <span className="a-footer c-brand__first-word t-title__first-word">{data.brand.firstName}</span>
                          <span className="a-footer c-brand__second-word t-title__second-word">{data.brand.lastName}</span>
                        </span>
                      </h1>
                    </div>
                  </div>
                </div>

                <ul className="c-footer__social-buttons c-social-buttons o-content__body">
                  {(data.socialLinks || []).map((link) => (
                    <li className="a-footer" key={link.id}>
                      <a href={link.href} target="_blank" rel="noopener noreferrer" className="c-social-button t-social-button" aria-label={link.label}>
                        <i className={link.iconClass}></i>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Footer;
